import * as Sentry from '@sentry/react';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { toast } from '@components/common/Toast/Toast';

type Props = { children: ReactNode };
export type ErrorContextType = { error: Error | null | undefined, setError: ( value: Error | null ) => void, handleError: ( error: Error ) => void; };
export const errorContext = createContext<ErrorContextType | null>( null );


export const ProvideError = ({ children }: Props ) => {
  const _error = useProvideError();
  return <errorContext.Provider value={_error}>{children}</errorContext.Provider>;
};

export default function useError() {
  return useContext( errorContext );
}

function useProvideError() {
  const [ error, setError ] = useState<Error | null>();
 
  const handleError = ( error: Error ) => {
    setError( error );
    Sentry.captureException( error );
  };

  useEffect(() => {
    if ( error ) {
      toast.error({
        title: error.name || 'Error',
        message: error.message,
        onClose: () => setError( null )
      });
    }
  }, [error]);
  useEffect(() => {
    const onUnhandledRejection = ( event: PromiseRejectionEvent ) => {
      handleError( event.reason instanceof Error ? event.reason : new Error( String( event.reason )));
    };
    const onError = ( event: ErrorEvent ) => {
      if ( event.error ) {
        handleError( event.error );
      } else {
        handleError( new Error( event.message ));
      }
    };
  
    window.addEventListener( 'unhandledrejection', onUnhandledRejection );
    window.addEventListener( 'error', onError );
    return () => {
      window.removeEventListener( 'unhandledrejection', onUnhandledRejection );
      window.removeEventListener( 'error', onError );
    };
  }, [handleError]);
  

  return {
    error,
    setError,
    handleError
  };
}
